import { ArrowUpRight } from "lucide-react";
import { Line, Reveal, Overline } from "@/components/Kinetic";
import WhyGadulabs from "@/components/home/WhyGadulabs";
import TechnologySection from "@/components/home/TechnologySection";
import { useLang } from "@/i18n/LanguageContext";

const FACTS_EN = [
  { k: "Company", v: "GADULABS" },
  { k: "Base", v: "Türkiye" },
  { k: "Founded", v: "August 2026" },
  { k: "Focus", v: "Product engineering" },
  { k: "Flagship", v: "Cernio" },
];

const FACTS_TR = [
  { k: "Şirket", v: "GADULABS" },
  { k: "Merkez", v: "Türkiye" },
  { k: "Kuruluş", v: "Ağustos 2026" },
  { k: "Odak", v: "Yazılım ve ürün geliştirme" },
  { k: "Amiral gemisi", v: "Cernio" },
];

export default function CompanyPage() {
  const { lang, tx } = useLang();
  const facts = lang === "tr" ? FACTS_TR : FACTS_EN;


  return (
    <>
      <section className="bg-paper pt-32 sm:pt-40 lg:pt-44">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 sm:pb-28 lg:px-8">
          <Reveal immediate y={16}>
            <Overline>{tx("Company", "Şirket")}</Overline>
          </Reveal>
          <h1
            data-testid="company-hero-title"
            className="mt-7 max-w-4xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          >
            {lang === "tr" ? (
              <>
                <Line delay={0.1}>Gerçek sorunların etrafında</Line>
                <Line delay={0.22}>
                  <span className="text-slate-400">yazılım geliştiriyoruz.</span>
                </Line>
              </>
            ) : (
              <>
                <Line delay={0.1}>We build software</Line>
                <Line delay={0.22}>
                  <span className="text-slate-400">around real problems.</span>
                </Line>
              </>
            )}
          </h1>
          <Reveal immediate delay={0.45} y={20} className="mt-8 max-w-2xl">
            <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
              {tx(
                "GADULABS is a product-focused software company based in Türkiye, building intelligent digital products for international businesses. We combine software engineering, applied AI and practical domain knowledge to turn complex workflows into focused software products.",
                "GADULABS, Türkiye merkezli, ürün odaklı bir yazılım şirketidir; uluslararası işletmeler için akıllı dijital ürünler geliştirir. Karmaşık iş akışlarını odaklı yazılım ürünlerine dönüştürmek için yazılım mühendisliğini, uygulamalı yapay zekâyı ve pratik alan bilgisini bir araya getirir.",
              )}
            </p>
          </Reveal>
          <Reveal immediate delay={0.58} y={20} className="mt-8 max-w-2xl">
            <p className="text-base leading-relaxed text-slate-600">
              {tx(
                "Depending on the problem, our work takes different forms — web applications, SaaS products, mobile apps, corporate websites, AI-powered software. We are deliberately small and focused: we build products we own, and take on selected work — like DOSE Modernization — where software meets a real operating business. Our flagship product is Cernio.",
                "Soruna bağlı olarak çalışmalarımız farklı biçimler alır — web uygulamaları, SaaS ürünleri, mobil uygulamalar, kurumsal web siteleri, yapay zekâ destekli yazılımlar. Bilinçli olarak küçük ve odaklıyız: sahibi olduğumuz ürünler geliştirir ve DOSE Modernization gibi, yazılımın gerçek bir işletmeyle buluştuğu seçili işler üstleniriz. Amiral gemisi ürünümüz Cernio'dur.",
              )}
            </p>
          </Reveal>
          <Reveal immediate delay={0.7} y={20}>
            <dl className="mt-14 grid grid-cols-2 gap-x-8 gap-y-8 border-t border-slate-200 pt-10 sm:grid-cols-5">
              {facts.map((f, i) => (
                <div key={f.k} data-testid={`company-fact-${["company", "base", "founded", "focus", "flagship"][i]}`}>
                  <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                    {f.k}
                  </dt>
                  <dd className="mt-2 font-display text-base font-bold text-ink">
                    {f.v}
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      <WhyGadulabs />
      <TechnologySection />

      <section id="contact" className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{tx("Contact", "İletişim")}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
              {tx("Talk to us.", "Bize yazın.")}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">
              {tx(
                "For product inquiries about Cernio, partnerships, or anything else — one address reaches the whole team.",
                "Cernio ile ilgili ürün soruları, iş ortaklıkları veya başka herhangi bir konu — tek adres tüm ekibe ulaşır.",
              )}
            </p>
          </Reveal>
          <Reveal delay={0.24} className="mt-10">
            <a
              href="mailto:info@gadulabs.com"
              data-testid="contact-mailto-button"
              className="focus-ring group inline-flex items-center gap-3 border border-ink bg-ink px-7 py-4 font-display text-lg font-bold tracking-tight text-white transition-colors duration-300 hover:bg-white hover:text-ink sm:text-xl"
            >
              info@gadulabs.com
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </Reveal>
          <Reveal delay={0.32}>
            <div
              data-testid="company-legal-block"
              className="mt-12 max-w-xl border-t border-slate-200 pt-8"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                {tx("Legal identity", "Yasal kimlik")}
              </p>
              <p className="mt-3 font-display text-base font-bold text-ink">
                GADULABS YAZILIM LİMİTED ŞİRKETİ
              </p>
              <address className="mt-3 text-sm not-italic leading-relaxed text-slate-600">
                Mustafa Kemal Paşa Mah. Yalova-Bursa Yolu Cad.
                <br />
                A Blok 1. Kademe No: 123/1, İç Kapı No: 10
                <br />
                Merkez / Yalova, Türkiye
              </address>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
